import { generateClient } from 'aws-amplify/api';
const client = generateClient();

/**
 * Generates a character backstory using the LLM service
 * 
 * @param {Object} characterInfo - Character information
 * @param {string} characterInfo.name - Character name
 * @param {string} characterInfo.race - Character race
 * @param {string} characterInfo.background - Character background (scholar, soldier, etc)
 * @param {string} characterInfo.alignment - Character alignment
 * @param {Object} characterInfo.skills - Character skills with categories and ranks
 * @param {Object} characterInfo.attributes - Character attributes (STR, DEX, etc)
 * @param {Object} characterInfo.personality - Character personality traits
 * @returns {Promise<Object>} - Generated backstory and personality elements
 */
export const generateBackstory = async (characterInfo) => {
  // Extract the top 3-5 skills for narrative focus
  const topSkills = extractTopSkills(characterInfo.skills);
  
  try {
    // Build the input for the backstory generation
    const input = {
      characterName: characterInfo.name,
      characterRace: characterInfo.race,
      characterAlignment: characterInfo.alignment,
      characterBackground: characterInfo.background,
      characterAttributes: JSON.stringify(characterInfo.attributes),
      characterSkills: JSON.stringify(topSkills),
      personalityTraits: characterInfo.personality?.traits || '',
      ideals: characterInfo.personality?.ideals || '',
      bonds: characterInfo.personality?.bonds || '',
      flaws: characterInfo.personality?.flaws || '',
    };

    // Call the GraphQL API to generate backstory
    const result = await client.graphql({
      query: `
        query GenerateBackstory($input: BackstoryInput!) {
          generateBackstory(input: $input) {
            narrative
          }
        }
      `,
      variables: { input }
    });

    // Process the response
    const narrative = result.data.generateBackstory.narrative;
    
    // Extract personality elements if they weren't provided
    let personalityElements = {};
    if (!characterInfo.personality?.traits) {
      // Use simple extraction logic or return empty values
      personalityElements = extractPersonalityFromNarrative(narrative);
    }

    return {
      narrative,
      ...personalityElements
    };
  } catch (error) {
    console.error('Error generating backstory:', error);
    throw new Error('Failed to generate character backstory');
  }
};

/**
 * Extracts top skills from the character's skill set for narrative focus
 * 
 * @param {Object} skills - Character skills with categories and ranks
 * @returns {Array} - Array of the character's top skills
 */
const extractTopSkills = (skills) => {
  if (!skills) return [];

  // Convert the skills object to an array of {category, skill, rank} objects
  const flatSkills = [];
  
  Object.entries(skills).forEach(([category, skillSet]) => {
    Object.entries(skillSet).forEach(([skill, rank]) => {
      if (rank > 0) {
        flatSkills.push({ category, skill, rank });
      }
    });
  });
  
  // Sort by rank (descending) and take the top 5
  return flatSkills
    .sort((a, b) => b.rank - a.rank)
    .slice(0, 5)
    .map(item => `${item.skill} (${item.rank})`);
};

/**
 * Simple utility to extract personality elements from the generated narrative
 * 
 * @param {string} narrative - The generated backstory narrative
 * @returns {Object} - Extracted personality elements
 */
const extractPersonalityFromNarrative = (narrative) => {
  // This is a simplified approach - in a real implementation, you might 
  // make another call to the LLM to extract these elements
  return {
    personalityTraits: '',
    ideals: '',
    bonds: '',
    flaws: ''
  };
};

/**
 * Generates a character name based on character details
 * 
 * @param {Object} characterInfo - Basic character information
 * @returns {Promise<Object>} - Generated name
 */
export const generateCharacterName = async (characterInfo) => {
  try {
    const input = {
      characterRace: characterInfo.race,
      characterAlignment: characterInfo.alignment,
      characterBackground: characterInfo.background
    };

    const result = await client.graphql({
      query: `
        query GenerateCharacterName($input: CharacterNameInput!) {
          generateCharacterName(input: $input) {
            name
          }
        }
      `,
      variables: { input }
    });

    return {
      name: result.data.generateCharacterName.name
    };
  } catch (error) {
    console.error('Error generating character name:', error);
    throw new Error('Failed to generate character name');
  }
};